import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { BRAND } from '@br/protocol';
import { errorMessage } from '../api/client';
import { useAcceptUserInvite, useUserInvite } from '../api/queries';
import { Alert, Button, Field, FullPageSpinner, Input } from '../components/ui';
import { formatDateTime } from '../lib/format';
import { ROLE_LABEL } from '../lib/labels';
import { usePageTitle } from '../lib/title';

export function InviteAcceptPage() {
  usePageTitle('운영자 가입');
  const { token = '' } = useParams<{ token: string }>();
  const invite = useUserInvite(token);
  const accept = useAcceptUserInvite();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (invite.data?.email) setEmail(invite.data.email);
  }, [invite.data?.email]);

  if (invite.isPending) return <FullPageSpinner />;
  if (invite.isError)
    return (
      <div className="mx-auto mt-24 max-w-md px-4">
        <Alert level="error">
          초대 링크가 유효하지 않거나 만료되었습니다. 관리자에게 새 링크를 요청하세요.
        </Alert>
      </div>
    );
  const inv = invite.data;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError('비밀번호는 8자 이상이어야 합니다');
    if (password !== confirm) return setError('비밀번호가 서로 다릅니다');
    accept.mutate(
      { token, name: name.trim(), email: email.trim(), password },
      {
        onSuccess: () => navigate('/profiles', { replace: true }),
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-line bg-surface p-8 shadow-sm"
      >
        <h1 className="text-xl font-bold text-ink">{BRAND.name}</h1>
        <p className="mt-1 text-sm text-ink-muted">
          <strong>{inv.orgName}</strong>의 {ROLE_LABEL[inv.role]}로 초대되었습니다.
          <br />
          <span className="text-xs">초대 만료: {formatDateTime(inv.expiresAt)}</span>
        </p>
        <div className="mt-6 space-y-4">
          <Field label="이름" required>
            <Input
              autoFocus
              required
              maxLength={64}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field
            label="이메일"
            required
            hint={inv.email ? '초대에 지정된 이메일입니다' : undefined}
          >
            <Input
              type="email"
              required
              value={email}
              disabled={Boolean(inv.email)}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
            />
          </Field>
          <Field label="비밀번호" required hint="8자 이상">
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Field label="비밀번호 확인" required>
            <Input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          {error && <Alert level="error">{error}</Alert>}
          <Button type="submit" variant="primary" className="w-full" loading={accept.isPending}>
            가입하고 시작하기
          </Button>
        </div>
      </form>
    </div>
  );
}
